import { Component, forwardRef, Input, inject, signal, Output, EventEmitter } from '@angular/core';
import { ReactiveFormsModule, FormsModule, FormGroup, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { QuillEditorComponent } from 'ngx-quill';
import { TableService } from '../../services/table.service';
import { ImageUploadService } from '../../services/image-upload.service';
import { ToastService } from '../../services/toast.service';
import { LoggerService } from '../../services/logger.service';
import { CommonModule } from '@angular/common';

const WYSIWYG_CONTROL_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => WysiwygEditorComponent),
  multi: true
}

@Component({
    selector: 'app-wysiwyg-editor',
    imports: [ReactiveFormsModule, QuillEditorComponent, FormsModule, CommonModule],
    providers: [WYSIWYG_CONTROL_ACCESSOR],
    styleUrl: './wysiwyg-editor.component.scss',
    template: `
    <ng-content select="label"></ng-content>
    @if ( imageError()) {
      <div class="alert alert-danger" role="alert">
        {{  imageError() }}
      </div>
    }
    <div [formGroup]="parentForm" [attr.data-testid]="id + '-test'">
      <quill-editor (onEditorCreated)="getEditorInstance($event)"
        [id]="id"
        [placeholder]="placeholder"
        [formControlName]="formControlName"
        [className]="'wysiwyg-editor-component'"
        [modules]="{toolbar: toolbarOptions}"
        [disabled]="isUploading()"
      ></quill-editor>
      @if (isUploading()) {
        <div class="upload-indicator">
          <div class="m-auto">
            <div class="m-auto me-2 spinner-border spinner-border-sm text-primary" role="status">
              <span class="visually-hidden">Uploading image...</span>
            </div>
            <span class="m-auto">Uploading image...</span>
          </div>
        </div>
      }
    </div>`
})
export class WysiwygEditorComponent implements ControlValueAccessor {
  @Input() placeholder = 'Enter text here';
  @Input() id = 'editor';
  @Input() parentForm!: FormGroup;
  @Input() formControlName!: string;
  @Input() quizGuid!: string;

  quillEditorRef: any;
  isUploading = signal(false);
  imageError = signal<string>('');

  private tableService = inject(TableService);
  private imageUploadService = inject(ImageUploadService);
  private toastService = inject(ToastService);
  private logger = inject(LoggerService);

  getEditorInstance(editorInstance: any) {
    this.quillEditorRef = editorInstance;
    const toolbar = this.quillEditorRef.getModule('toolbar');
    toolbar.addHandler('image', this.uploadImageHandler);
    // The Quill table button by default inserts a single row and nothing else, so overriding it for now.
    toolbar.addHandler('table', () => {
      this.tableService.promptTableSize((rows, cols) => {
        this.tableService.insertTable(rows, cols, this.quillEditorRef);
      });
    });
    toolbar.addHandler('table-cell-color-picker', this.tableCellColorPickerHandler);
  }

  uploadImageHandler = () => {
    if (!this.quizGuid) {
      this.logger.error('Quiz GUID is required for image uploads');
      return;
    }

    this.imageError.set('');

    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      const file = input.files?.length ? input.files[0] : null;
      const range = this.quillEditorRef.getSelection();

      if (file) {
        this.isUploading.set(true);
        this.imageUploadService.uploadImage(this.quizGuid, file).subscribe({
          next: (response) => {
            // Insert the image into the editor
            this.quillEditorRef.insertEmbed(range.index, 'image', response.image_url);

            const currentValue = this.quillEditorRef.root?.innerHTML || '';
            const control = this.parentForm.get(this.formControlName);
            if (control) {
              control.setValue(currentValue);
              control.markAsDirty();
              control.updateValueAndValidity();
            }

            this.parentForm.markAsDirty();
            this.isUploading.set(false);
          },
          error: (error) => {
            this.logger.error('Failed to upload image', error);
            this.toastService.error('Failed to upload image');
            this.handleImageUploadError(error);
            this.isUploading.set(false);
          }
        });
      }
    }
  }

  private handleImageUploadError(err: any): void {
    const errorMessage = err?.error?.error || err?.message || 'Failed to upload image. Please try again.';
    this.imageError.set(errorMessage);
  }

  tableCellColorPickerHandler = () => {
    alert('Not Yet Implemented');
  }

  toolbarConfigDefault = [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    [{ 'align': [] }],
    ['link', 'image', 'video'],
    // TODO: Figure out how to get an icon to show on the table cell color picker
    ['table', 'table-cell-color-picker']
  ];

  @Input() toolbarOptions = this.toolbarConfigDefault;

  writeValue(obj: any): void {}
  registerOnChange(fn: any): void {}
  registerOnTouched(fn: any): void {}
  setDisabledState?(isDisabled: boolean): void {}
}
